import SearchAPI from '../../api/search';
import types from '../mutation-types';

const defaultMeta = () => ({
  total_count: 0,
  current_page: 1,
  per_page: 25,
  total_pages: 0,
});

const normalizeSearchPayload = payload => {
  if (Array.isArray(payload)) {
    return { records: payload, meta: defaultMeta(), append: false };
  }

  return {
    records: payload.records || [],
    meta: payload.meta || defaultMeta(),
    append: payload.append || false,
  };
};

export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  meta: {
    contacts: defaultMeta(),
    conversations: defaultMeta(),
    messages: defaultMeta(),
  },
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getMeta(state) {
    return state.meta;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch }, payload) {
    const { q, ...searchParams } = payload;
    if (!q) {
      return;
    }
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      await Promise.all([
        dispatch('contactSearch', { q, ...searchParams }),
        dispatch('conversationSearch', { q, ...searchParams }),
        dispatch('messageSearch', { q, ...searchParams }),
      ]);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.FULL_SEARCH_SET_UI_FLAG, {
        isFetching: false,
        isSearchCompleted: true,
      });
    }
  },
  async contactSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    if (page === 1) {
      commit(types.CONTACT_SEARCH_SET, []);
    }
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ ...searchParams, page });
      commit(types.CONTACT_SEARCH_SET, {
        records: data.payload.contacts,
        meta: data.meta,
        append: page > 1,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async conversationSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    if (page === 1) {
      commit(types.CONVERSATION_SEARCH_SET, []);
    }
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.conversations({
        ...searchParams,
        page,
      });
      commit(types.CONVERSATION_SEARCH_SET, {
        records: data.payload.conversations,
        meta: data.meta,
        append: page > 1,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async messageSearch({ commit }, payload) {
    const { page = 1, ...searchParams } = payload;
    if (page === 1) {
      commit(types.MESSAGE_SEARCH_SET, []);
    }
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ ...searchParams, page });
      commit(types.MESSAGE_SEARCH_SET, {
        records: data.payload.messages,
        meta: data.meta,
        append: page > 1,
      });
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
    }
  },
  async clearSearchResults({ commit }) {
    commit(types.MESSAGE_SEARCH_SET, []);
    commit(types.CONVERSATION_SEARCH_SET, []);
    commit(types.CONTACT_SEARCH_SET, []);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, payload) {
    const { records, meta, append } = normalizeSearchPayload(payload);
    state.contactRecords = append
      ? [...state.contactRecords, ...records]
      : records;
    state.meta.contacts = meta;
  },
  [types.CONVERSATION_SEARCH_SET](state, payload) {
    const { records, meta, append } = normalizeSearchPayload(payload);
    state.conversationRecords = append
      ? [...state.conversationRecords, ...records]
      : records;
    state.meta.conversations = meta;
  },
  [types.MESSAGE_SEARCH_SET](state, payload) {
    const { records, meta, append } = normalizeSearchPayload(payload);
    state.messageRecords = append ? [...state.messageRecords, ...records] : records;
    state.meta.messages = meta;
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
